import type { Role } from '../lib/auth';

/**
 * "How this works" for every staff screen, in plain words for people who are not technical.
 *
 * Each entry has 3–5 short lines, always in this order:
 *   1. what the page is for (also the one line shown for it on "What I can do"),
 *   2. what you can do here,
 *   3. what updates live (by itself, without reloading),
 *   4. one tip.
 *
 * Where a page means something different to different people (a shop admin, a counter login and the super
 * admin all open "Bills"), `roles` replaces the fields that differ. Keys are a role, or a group:
 * 'shop' (shop admin and counter logins) and 'venueWide' (the super admin and the accountant).
 * A role's own entry wins over its group's.
 */
export interface Help {
  title: string;
  /** Who uses this page, in a few words. */
  forWhom: string;
  lines: string[];
}

export type Audience = Role | 'shop' | 'venueWide';

export interface HelpDef extends Help {
  /** The route under /admin as written in App.tsx ('' for /admin itself), e.g. 'visits/:id'. */
  path: string;
  roles?: Partial<Record<Audience, Partial<Help>>>;
  /** Opens on its own (a printable page or the sign-in page), without the menu and the help button. */
  standalone?: boolean;
}

const LIVE_SELF = 'This page updates by itself when something changes (watch the green dot at the top).';
const SAVED_AT_ONCE = 'What you save here applies at once; others see it the next time their screen refreshes.';

export const HELP: HelpDef[] = [
  // ------------------------------------------------------------------ overview
  {
    path: 'dashboard',
    title: 'Dashboard',
    forWhom: 'The super admin, the accountant and the cashier',
    lines: [
      "Today's numbers for the whole venue at a glance: parking, shops, money in.",
      'Look at the cards and the daily trend; tap a card to open the page behind it.',
      LIVE_SELF,
      'Start the day here, then open the page you need from the menu.',
    ],
    roles: {
      cashier: {
        forWhom: 'The cashier',
        lines: [
          "Today's collections and vehicles at your gate at a glance.",
          'Check how much has come in and how many vehicles are inside or waiting to pay.',
          LIVE_SELF,
          'To take a payment, open Collect payment from the menu.',
        ],
      },
    },
  },
  {
    path: 'shops',
    title: 'All shops',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Profit and loss of every shop and outlet side by side for the period you pick.',
      'Pick a period, sort the list, download Excel, or tap a shop to open it.',
      'New bills and expenses change the figures by themselves.',
      'Shops shown in red made a loss in this period. Tap "Open shop" to see why.',
    ],
  },
  {
    path: 'daily-pulse',
    title: 'Daily pulse',
    forWhom: 'The super admin and the accountant',
    lines: [
      'One day of the whole venue on one page: vehicles and visitors, who signed in, what each shop sold and spent, and the best sellers.',
      'Pick a business day; switch between Overview, Staff, Shops, Products and Gate, and search by name.',
      'Today\'s figures refresh every 30 seconds as vehicles come in and bills are made.',
      'For several days at once, or to download, use Daily report and Daily sales by shop.',
    ],
  },
  {
    path: 'shops/daily',
    title: 'Daily sales by shop',
    forWhom: 'The super admin and the accountant',
    lines: [
      'What every shop sold, day by day: bills, items, sales and profit, by product and by menu, plus low sellers.',
      'Pick Today, Yesterday, 7 days or a month; switch between By day, By shop, Products sold, By menu and Low sellers.',
      'Tap a day cell to see that shop\'s bills, payment modes and top products; pick one shop in the Shop box to focus on it.',
      'Figures match each shop\'s profit & loss to the paisa and update as bills come in.',
      'Low sellers: what did not sell and what sold least. With one shop picked, download its sales report as PDF or Excel.',
    ],
    roles: {
      shop: {
        title: 'Daily sales',
        forWhom: 'Shop admins',
        lines: [
          'What your shop sold day by day, product by product and menu by menu, and what did not sell.',
          'Pick Today, Yesterday, 7 days or a month; switch between By day, Products sold, By menu and Low sellers.',
          'Figures change by themselves as bills are made.',
          'Check Low sellers before ordering stock: it shows the last sale and what is left on the shelf.',
          'Sales report PDF or Excel: totals, average bill, best sellers, busy hours and payment modes for the period.',
        ],
      },
    },
  },
  {
    path: 'shops/open-days',
    title: 'Shop open days',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Which days each shop was open or closed in a month.',
      'Open: marked open on Shops open today, a bill, a clock-in, or a counter login signed in (not the shop admin).',
      'Pick a month and a shop for its calendar (tap a day for the details), or see every shop in one table; download CSV or Excel.',
      'This month updates by itself as bills are made and people sign in.',
      'Red days with nothing logged: ask the shop, and record agreed closures in the Closure register so the month reads right.',
    ],
    roles: {
      shop: {
        title: 'Open days',
        forWhom: 'Shop admins',
        lines: [
          'Which days your shop was open or closed this month, from your bills, clock-ins and sign-ins.',
          'Pick a month; tap a day to see what was recorded on it; download CSV or Excel.',
          'Today changes by itself as soon as someone bills or signs in.',
          'When you plan to stay closed, give the venue notice from Venue statements → Report closure, so the day shows as planned.',
        ],
      },
    },
  },
  {
    path: 'shops/today',
    title: 'Shops open today',
    forWhom: 'The super admin and the people they choose (e.g. Sanjay at the gate); the accountant can view it',
    lines: [
      'Mark which shops opened today, and at what time, and which stayed closed (shop closed, with the reason if known).',
      'Tap Opened or Closed on each shop (or Mark all not-marked as open), then Save. The Month report tab counts the days per shop.',
      'Each card also shows what the system saw by itself (first bill, clock-in or sign-in) and any closure in the Closure register.',
      'You can mark today and yesterday; changing a shop someone else marked asks for a reason. Only the super admin changes older days.',
    ],
    roles: {
      accountant: {
        forWhom: 'The accountant (view only)',
        lines: [
          'Which shops opened today, and at what time, and which stayed closed, as marked at the venue.',
          'Pick a day, or open the Month report tab for the open and closed days per shop and a CSV download.',
          'Marks appear as soon as they are saved; each card also shows the first bill, clock-in or sign-in of the day.',
          'Marking is done by the super admin and the people they choose.',
        ],
      },
    },
  },
  {
    path: 'reports/day',
    title: 'Day report (PDF)',
    forWhom: 'The super admin and the accountant',
    lines: [
      "The whole venue's day in one report: the gate, visitors, every shop's bills, customers and sales, best and low sellers.",
      'Plus every product sold per shop, discounts, returns and how customers paid; who signed in or clocked in; every expense of the day.',
      "One tap from Today's full report on the Dashboard, the Daily report or the Daily pulse opens today; today's figures are as of now.",
      'Pick the day, then Download PDF to share or print it, or Download Excel to work with the figures (on a phone they stay at the top).',
      "Yesterday's PDF is saved every night: open it under Saved reports.",
    ],
  },
  {
    path: 'reports/daily',
    title: 'Daily report',
    forWhom: 'The super admin, the accountant and the cashier',
    lines: [
      "Each day's picture: vehicles in and out, how long they stayed, passengers, fees and what the shops sold.",
      'Pick Today, Yesterday, 7 days or a month; open Gate for stay times by vehicle type and hour, Shops for the shops.',
      "Today's figures change by themselves as vehicles come and go and bills are made.",
      'Download CSV or Excel to keep the day on file.',
    ],
    roles: {
      cashier: {
        forWhom: 'The cashier',
        lines: [
          "Your gate's day: vehicles in and out, how long they stayed, passengers and fees.",
          'Pick Today, Yesterday, 7 days or a month; open Gate for stay times by vehicle type and hour.',
          "Today's figures change by themselves as vehicles come and go.",
          'A vehicle counts as "in" on the day it entered and "out" on the day it left.',
        ],
      },
    },
  },
  {
    path: 'live-activity',
    title: 'Live activity',
    forWhom: 'Everyone',
    lines: [
      'A running list of what is happening right now: entries, exits, bills, payments.',
      'Scroll the list or filter it; tap an item to open it.',
      'New items appear at the top by themselves. You only see what your login may see.',
      'If the dot at the top is grey, the list still refreshes every few seconds.',
    ],
    roles: {
      shop: {
        forWhom: 'Your shop team',
        lines: [
          "A running list of what is happening in your shop: bills, returns, stock changes.",
          'Scroll the list; tap a bill to open it.',
          'New items appear at the top by themselves. Other shops are never shown here.',
          'Keep this open on a second screen to watch a busy counter.',
        ],
      },
    },
  },

  {
    path: 'reports/visitors',
    title: 'Visitors',
    forWhom: 'The super admin, the accountant and the cashier (gate staff for their own gate)',
    lines: [
      'Who comes to the venue, day by day: vehicles in, new against returning, passengers, where they are from and how long they stayed.',
      'Pick a period, then switch between By day, By origin (district or state from the number plate) and By vehicle type; download CSV or Excel.',
      'The figures change by themselves as the gate records entries and exits.',
      'A vehicle is "new" on its first visit ever; the repeat rate is the share of arrivals that had visited before.',
    ],
  },
  {
    path: 'presence',
    title: "Who's working",
    forWhom: 'The super admin and the accountant (every shop and team); shop admins (their own shop)',
    lines: [
      'Who is at work right now, from Clock in and Clock out: per shop, gate and team, since when, hours today and last activity.',
      'Working now: who is on duty, clocked out or not in today. Attendance: shifts and hours for a period, with CSV and Excel.',
      'Clock-ins show here within seconds; "Working" means clocked in and active in the last 10 minutes.',
      'A forgotten shift closes after 12 hours or at day end; fix it with Correct and a reason (the first time stays on record).',
    ],
    roles: {
      shop_admin: {
        forWhom: 'You, the shop admin: your own shop’s staff only',
        lines: [
          'Which of your staff clocked in today, since when and for how long; Attendance shows their hours for any period.',
          'Each person starts and ends their shift with the Clock in button at the top of the screen.',
          'Updates by itself as your staff clock in and out.',
          'Correct a forgotten clock-out with a reason; your own shifts are corrected by the super admin.',
        ],
      },
    },
  },

  // ------------------------------------------------------------------ parking
  {
    path: 'security',
    title: 'Security dashboard',
    forWhom: 'Gate security and the super admin',
    lines: [
      'Your phone screen at the gate: a photo and the number plate of every vehicle that comes in, and one tap when it leaves.',
      'New vehicle: photo, check the plate read from it (or type it), type, passengers, Save. Vehicle leaving: find it, tap Exit.',
      "Vehicles inside and today's counts update by themselves, at least every 30 seconds.",
      'No network? Entries and exits wait on the phone ("Waiting to send") and are sent by themselves. Keep the page open.',
      'Forgot yesterday\'s vehicle? Tap "Entry was earlier", set when it came (and left), give a reason, Record earlier entry.',
    ],
  },
  {
    path: 'gate',
    title: 'Gate & vehicles inside',
    forWhom: 'Gate security and the super admin',
    lines: [
      'Record every vehicle that comes in and goes out at this gate: parking is free, so an exit just closes the visit.',
      'Photo, check the plate read from it, passengers, Record entry; tap Exit when it leaves. Forgot yesterday\'s vehicle? "Entry was earlier".',
      'The list of vehicles inside updates by itself, and at least every 30 seconds.',
      'Under the plate you see where the vehicle is from (state and district), its earlier visits, and if it is already inside.',
      'Made a slip? Fix your own entry for 30 min, undo it for 15 min, or "Delete completely" until the day ends.',
    ],
  },
  {
    path: 'visitors',
    title: 'Visitors & passengers',
    forWhom: 'Gate staff and the super admin',
    lines: [
      'How many people (PAX) came in, counted at the gate, and the groups in each vehicle.',
      'Pick a day or a range to see totals by hour and by vehicle.',
      'Counts change by themselves as vehicles come in.',
      'Ask the gate to fill in passengers at entry, or this page stays empty.',
    ],
  },
  {
    path: 'live',
    title: 'Live park status',
    forWhom: 'The super admin, the accountant and the cashier',
    lines: [
      'How busy the parking is right now: vehicles inside, by gate and vehicle type.',
      'See who came in recently, how long vehicles have stayed and any overstays.',
      LIVE_SELF,
      'Overstays are vehicles parked longer than the allowed time.',
    ],
  },
  {
    path: 'cctv',
    title: 'Live cameras',
    forWhom: 'The super admin, and gate staff the owner allows',
    lines: [
      'Watch the parking cameras live, with the number plates they read.',
      'Tap Stop, Refresh or Full screen on a camera; owners open Gate PC details to see what is wrong.',
      'Video plays live through the gate PC, which must stay on; each camera rechecks every 15 seconds.',
      'Plates are read only while the gate PC is on. The NVR keeps recording video on its own.',
    ],
  },
  {
    path: 'collect',
    title: 'Collect payment',
    forWhom: 'The cashier and the super admin',
    lines: [
      'Take the parking fee from vehicles that have exited (only where the venue charges for parking).',
      'Pick the vehicle, choose cash, UPI, card or wallet, then confirm. Print the slip if asked.',
      'Vehicles waiting to pay appear by themselves when the gate records an exit.',
      'Parking is free at Carnival City, so this page only says so; the owner can switch charging on under Settings.',
    ],
  },
  {
    path: 'visits',
    title: 'Visit history',
    forWhom: 'Gate staff, the cashier and the super admin',
    lines: [
      'Every vehicle entry, searchable by number plate.',
      'Search a plate or filter by date and status; tap a visit to see its details.',
      'New visits are added by themselves.',
      'Type only part of the plate (e.g. 1234) to find it quickly.',
      'The super admin can delete (restorable) or delete completely; a guard can delete their own entry completely the same day.',
    ],
  },
  {
    path: 'visits/:id',
    title: 'Visit details',
    forWhom: 'Gate staff, the cashier and the super admin',
    lines: [
      'One vehicle visit: when it came in and out, the fee, photos and payments.',
      'Reprint the entry pass, record the exit or open the payment.',
      'Payments and the exit appear by themselves when someone records them.',
      'Delete completely removes an entry as if never made (super admin; the guard on their own entry the same day).',
      'Report a mistake sends the owner a note with a link to this entry; gate staff can undo their own entry or exit for 15 minutes.',
    ],
  },
  {
    path: 'vehicles/:id',
    title: 'Vehicle',
    forWhom: 'Gate staff, the cashier and the super admin',
    lines: [
      'One vehicle and all its visits to the venue.',
      'See how often it came, what it paid and which customer it belongs to.',
      'New visits of this vehicle show up by themselves.',
      'Link the vehicle to a customer so their app shows their visits.',
    ],
  },
  {
    path: 'printer',
    title: 'Printer',
    forWhom: 'Counters and the gate',
    lines: [
      'Connect the receipt printer used on this device: USB cable, Bluetooth, COM port or the print dialog.',
      'Pick the way marked "Best on this device": it suits this computer or phone. Cable and Bluetooth printers then print with no dialog.',
      "If the cable printer can't be reached, the screen says why and offers Reconnect; the print window is used only when you tap it.",
      'This setting is kept on this device only; set it on each counter phone or PC.',
      'Windows using the printer with its own driver? Choose the COM port, or open "Print without the dialog".',
    ],
    roles: {
      shop: {
        forWhom: 'Your shop counter',
        lines: [
          'Connect the receipt printer used for bills on this device: USB cable, Bluetooth, COM port or the print dialog.',
          'Pick the way marked "Best on this device": it suits this computer or phone. Cable and Bluetooth printers then print bills with no dialog.',
          "If the cable printer can't be reached, the screen says why and offers Reconnect; the print window is used only when you tap it.",
          'Set it once on each counter phone or PC; it is kept on that device.',
          'Windows using the printer with its own driver? Choose the COM port, or open "Print without the dialog".',
        ],
      },
      shop_admin: {
        forWhom: 'Your shop and its counters',
        lines: [
          'Printer for every counter: the venue default reaches each counter; save a model, position or width here to use your own.',
          '"Use the venue default" goes back to the super admin’s printer; "Send again to every counter" brings counters back to it.',
          'Counters shows how each counter prints and if its last print worked; amber marks where it differs from the shop printer.',
          "Connect this device below; if the cable printer can't be reached, the screen says why and offers Reconnect or the print window.",
          'Windows using the printer with its own driver? Choose the COM port, or open "Print without the dialog".',
        ],
      },
      owner: {
        forWhom: 'The super admin',
        lines: [
          'Printer for every shop (Settings): one model, position and width for every shop, new shops too, unless a shop sets its own.',
          'Open a shop to set its own printer or choose Use the venue default (shops the venue runs); a tenant shop’s admin sets theirs.',
          'Counters shows how each counter of that shop prints and whether its last print worked.',
          "Connect this device below; if the cable printer can't be reached, the screen says why and offers Reconnect or the print window.",
          'This device’s own setting is kept on this device only.',
        ],
      },
    },
  },

  // ------------------------------------------------------------------ shops (the venue's view)
  {
    path: 'outlets',
    title: 'Shops & outlets',
    forWhom: 'The super admin',
    lines: [
      'Every parking gate and shop at the venue, with its admin login. Each shop admin sees only their own shop, never another one.',
      'Add a shop with its rent agreement and collection day (or upload many at once), edit its details, or switch it off.',
      SAVED_AT_ONCE,
      'Delete signs its logins out at once; bills stay in past reports marked (deleted). Restore it from the Deleted tab.',
      'A spa or salon: tick “Takes appointments” (Fin & Flex spa is already set up with its services); then add its admin login.',
    ],
  },

  // ------------------------------------------------------------------ a shop's own pages
  {
    path: 'shop-home',
    title: 'Shop home',
    forWhom: 'Shop admins and counters',
    lines: [
      "Your shop's day at a glance: sales so far, bills, items running low; start billing, add an expense or close the day here.",
      'Sales analysis: pick a period (up to Since the start) and Daily, Weekly or Monthly; colour charts; PDF or Excel download.',
      'Revenue, Spent and Profit match Profit & loss; Menu analysis shows items rising ↑ or falling ↓. Figures update by themselves.',
      'A spa (Fin & Flex spa) opens on its day: today\'s list with Arrive / Start / Done / Pay, next up, this week, New appointment.',
      'Shop admin: "Dues to the venue" lists every open charge and the total; renovation charges show paid or not.',
    ],
    roles: {
      shop_staff: {
        forWhom: 'Counter staff',
        lines: [
          'Your counter at a glance: your bills today and items running low.',
          'Tap Start billing to make a bill, or Reprint to print one of today\'s bills again.',
          'Your bills and stock change by themselves.',
          'Ask your shop admin if you need to see more (for example the shop\'s takings).',
        ],
      },
      venueWide: {
        forWhom: 'The super admin and the accountant',
        lines: [
          "This shop's day at a glance: sales, bills and items running low.",
          'Look only: the shop admin runs the shop. Use "← All shops" to go back.',
          'Figures change by themselves as the shop makes bills.',
          'Use Profit & loss in the menu for this shop\'s full figures.',
          'Sales analysis: Daily, Weekly or Monthly charts, revenue, spent and profit, the menu rising or falling; PDF or Excel.',
        ],
      },
    },
  },
  {
    path: 'pos',
    title: 'Billing',
    forWhom: 'Shop counters and shop admins',
    lines: [
      'Make a bill: tap items (or scan a code), add a customer if they want, take payment, then Complete sale (F9).',
      'An offer code from the customer goes in "+ Offer code"; some offers need the customer\'s app QR scanned first.',
      'The last bill stays on top with the change to give, Print and View; tick "Print the receipt automatically" to print each sale.',
      "Bills print on this screen. If the cable printer can't be reached, the screen says why and offers Reconnect or the print window.",
      'Edit bill (Bills, View): add an item or change a quantity the same day; only the difference is collected or given back, then it reprints.',
    ],
    roles: {
      owner: {
        forWhom: 'The super admin at a venue-run counter',
        lines: [
          'Make a bill at this venue-run counter.',
          'Tap items, change quantities, then take payment.',
          'The next bill starts straight away; the last bill stays on top of the bill with Print and View.',
          'Switch on "Work at this counter" in the shop bar to bill here.',
        ],
      },
    },
  },
  {
    path: 'bills',
    title: 'Bills',
    forWhom: 'Shop admins, the super admin and the accountant',
    lines: [
      "Every bill your shop made, with the day's totals. New bills appear by themselves.",
      'Filter by date, status or cashier, search a bill or customer, download CSV or Excel.',
      'Edit bill: add an item or change a quantity the same day; only the difference is collected or given back, and the bill is reprinted.',
      'Tap a bill to reprint it or to take a return.',
      'Delete on a row removes a bill made by mistake completely (stock back, as if never billed); not if paid with points.',
    ],
    roles: {
      shop_staff: {
        forWhom: 'Counter staff',
        lines: [
          'The bills you made, starting with today.',
          'Search a bill or customer; pick "Everyone" to see the whole counter if allowed.',
          'Tap a bill to reprint it for the customer.',
          'Edit on your own bill (same day): add an item or change a quantity; only the difference is collected or given back.',
          'Billed by mistake? Delete removes your own bill completely the same day (stock back, as if never billed).',
        ],
      },
      venueWide: {
        forWhom: 'The super admin and the accountant',
        lines: [
          'Bills of every shop, or of one shop you pick.',
          'Filter by shop, date, status or cashier and download CSV or Excel.',
          'New bills appear by themselves.',
          'Pick one shop in the Shop box to see only its bills.',
        ],
      },
    },
  },
  {
    path: 'bills/:id',
    title: 'Bill',
    forWhom: 'Shop teams, the super admin and the accountant',
    lines: [
      'One bill: items, taxes, payments, returns and edits. A return never deletes the bill; it adds a return line.',
      'Reprint the bill, take a return or refund if allowed, or cancel a bill you made in the last 10 minutes (everything comes back).',
      'Edit bill: add an item or change a quantity the same day; only the difference is collected or given back, and the bill is reprinted.',
      'Delete completely: as if never billed (open day, no points or returns). Delete (return): everything back today.',
    ],
  },
  {
    path: 'bills/:id/edit',
    title: 'Edit bill',
    forWhom: 'The counter that made the bill, the shop admin and the super admin',
    lines: [
      'Add items or change quantities on a bill the same day; it keeps its number and reprints as Revised.',
      'Tap items to add them; use − and + or ✕ on the bill. The footer shows the new total, what was paid and the difference.',
      'More: collect only the difference. Less: it is given back the way the bill was paid, and the stock goes back.',
      'Not after the day is closed. If someone else changed the bill meanwhile, open it again.',
    ],
  },
  {
    path: 'products',
    title: 'Products & stock',
    forWhom: 'Shop admins',
    lines: [
      "Your shop's items, prices and stock.",
      'Add or edit products, record stock coming in, wastage or a count correction, import from a file.',
      'Stock goes down by itself as bills are made.',
      'Group items in categories so the counter finds them fast.',
      'Bulk upload: Download for Excel, change prices or stock, then Import → Add new and update existing. Items not in the file stay as they are.',
    ],
    roles: {
      shop_staff: {
        forWhom: 'Counter staff',
        lines: [
          "Your shop's items, prices and stock.",
          'Look up a price or how many are left. Changes need permission from your shop admin.',
          'Stock goes down by itself as bills are made.',
          'Tell your shop admin when something runs out.',
        ],
      },
      venueWide: {
        forWhom: 'The super admin and the accountant',
        lines: [
          "This shop's items, prices and stock.",
          'Look at prices, stock and stock movements.',
          'Stock goes down by itself as the shop makes bills.',
          'Each shop keeps its own list; another shop never sees it.',
          'Super admin: at a shop the venue runs, Download for Excel and Import → Add new and update existing change prices and stock in bulk.',
        ],
      },
    },
  },
  {
    path: 'inventory',
    title: 'Inventory',
    forWhom: 'A shop that keeps raw materials (e.g. BARSHA RESTAURANT)',
    lines: [
      'Inventory: raw materials like eggs and oil. Stock in when you buy, Used at the end of the day, Count to correct; bills use recipes.',
      'Stock in can also record the expense (tick it), so you do not type it again under Expenses. Wasted needs a reason.',
      'Set a recipe on a product (Products & stock → the product → Uses from inventory): every bill takes it, a return puts it back.',
      'Low or Out shows next to an item and on your home page. Daily report: opening, in, used, wasted and closing; Excel or PDF.',
      'Wrong entry? Open the item and tap “Correct this entry”. Stock may go below zero: billing is never stopped.',
    ],
    roles: {
      shop_staff: {
        forWhom: 'Counter staff the shop admin allowed',
        lines: [
          'Your shop’s raw materials (eggs, oil, vegetables) and how much is left.',
          'Tap Stock in when goods arrive, Used at the end of the day, Wasted for spoiled goods (say why), Count to correct.',
          'Bills take what their recipes use by themselves.',
          'Tell your shop admin when something shows Low or Out.',
        ],
      },
      venueWide: {
        forWhom: 'The super admin and the accountant',
        lines: [
          'This shop’s raw materials: stock now, what came in, was used, wasted, and the daily report as Excel or PDF.',
          'Switched on per shop under Shops & outlets → the shop → “Inventory of raw materials”.',
          'A shop run by its own admin is read only here; at a shop the venue runs the super admin can record entries.',
        ],
      },
    },
  },
  {
    path: 'shop-pnl',
    title: 'Profit & loss',
    forWhom: 'Shop admins, the super admin and the accountant',
    lines: [
      'How much your shop earned after costs, expenses and the venue\'s commission or share for the period you pick.',
      'Pick a period, see sales, cost of goods, expenses and profit, download it; links open the same days by menu and low sellers.',
      'New bills and expenses change the figures by themselves; the venue lines follow your rent terms (see Rent & venue charges).',
      'Add every expense under Expenses, or the profit will look higher than it is.',
    ],
    roles: {
      venueWide: {
        forWhom: 'The super admin and the accountant',
        lines: [
          "This shop's profit and loss for the period you pick.",
          'Pick a period, see sales, costs, venue commission and profit, download it.',
          'New bills and expenses change the figures by themselves.',
          'Use "All shops" to compare it with the other shops.',
        ],
      },
    },
  },
  {
    path: 'shop-staff',
    title: 'Staff logins',
    forWhom: 'Shop admins',
    lines: [
      "Sign-in logins for your shop's counters and team.",
      'Add a login, choose what it may do (bill, give discounts, see takings…), reset a password or switch it off.',
      'A change applies the next time that person opens a page.',
      'Give each person their own login, so every bill shows who made it.',
      'Delete a staff login: it is signed out at once and its name can be used again; its bills are kept.',
    ],
    roles: {
      venueWide: {
        forWhom: 'The super admin',
        lines: [
          "This shop's logins and what each may do.",
          'Look at who can sign in; the shop admin manages them.',
          'A change applies the next time that person opens a page.',
          'To add or reset any login yourself, use Staff users.',
          'To delete a shop admin login, use Staff users; to delete the whole shop, use Shops & outlets.',
        ],
      },
    },
  },
  {
    path: 'shop-settings',
    title: 'Receipt & shop details',
    forWhom: 'Shop admins',
    lines: [
      "Your shop's name, address, phone, logo and what the printed bill looks like.",
      'Change the details, upload a logo and preview the receipt.',
      SAVED_AT_ONCE,
      'GSTIN and tax type are set by the super admin; ask them if they are wrong.',
    ],
    roles: {
      venueWide: {
        forWhom: 'The super admin',
        lines: [
          "This shop's details and receipt layout.",
          'Look at or correct the details printed on its bills.',
          SAVED_AT_ONCE,
          'GSTIN, tax type and commission are changed under Shops & outlets.',
        ],
      },
    },
  },

  // ------------------------------------------------------------------ activities
  {
    path: 'games',
    title: 'Games & rides',
    forWhom: 'The cashier and the super admin',
    lines: [
      'The attractions, plays sold and which ones are working.',
      'Sell a play, mark a ride out of order or back in service.',
      'Plays and faults appear by themselves.',
      'Mark a broken ride at once so the counter stops selling it.',
      'Sold by mistake? Delete on Sales removes it completely (your own sale today; the manager any open day). "Refund instead" voids it.',
    ],
  },
  {
    path: 'turf',
    title: 'Turf booking',
    forWhom: 'The turf desk, the activities manager and the super admin',
    lines: [
      'The turf calendar: who booked which slot, a week per turf or every turf for a day. It opens on the outlet that has turfs.',
      'Book (walk-in or phone, add-ons, weekly series, packages), take an advance, collect the balance, cancel or mark a no-show.',
      'A known mobile fills the customer’s name (“Existing customer”); a new one is saved as a customer with the booking.',
      'Add time to a game in progress (+30 min, +1 h, +2 h) if the next slot is free: priced at the turf’s rate, collected at the desk.',
      'No turfs at this outlet? The page says where they are and offers to switch. A slot can never be booked twice.',
    ],
  },
  {
    path: 'karts',
    title: 'Go-kart track',
    forWhom: 'The kart desk, the track manager and the super admin',
    lines: [
      'The go-kart desk: every kart with its live timer, the queue with token numbers and rides waiting to pay.',
      'Add a rider (waiver and age ticked), start a kart, pause it, stop it: the bill is worked out by the rate; take the payment and print.',
      'Timers, the queue and payments update by themselves on every device.',
      'Open “TV display” on the screen at the track so riders see the next tokens.',
      'A ticket made by mistake? Delete removes it and its money completely (seller the same day, track manager, super admin).',
    ],
  },
  {
    path: 'karts/display',
    title: 'Go-kart queue display',
    forWhom: 'A TV or screen at the track',
    standalone: true,
    lines: [
      'Big screen for riders: the next tokens to come to the desk and who is on the track.',
      'Nothing to do here; tap “Full screen” once.',
      'It updates by itself whenever the desk starts or stops a kart or gives a token.',
    ],
  },
  {
    path: 'karts/fleet',
    title: 'Fleet & rates',
    forWhom: 'The track manager and the super admin',
    lines: [
      'The karts, the ride rates and the maintenance log of the track.',
      'Add or retire karts, set prices (per block, lap package or hourly, minimum, extras) and log services and repairs with cost.',
      SAVED_AT_ONCE,
      'Rides already in the queue keep the rate they were given.',
    ],
  },
  {
    path: 'karts/report',
    title: 'Rides report',
    forWhom: 'The track manager and the super admin',
    lines: [
      'Rides, minutes on the track and money by ride type, kart, day and hour, with the fleet’s utilisation and the desk prices used.',
      'Pick the days, then download every ride as CSV or Excel.',
      'Figures include rides finished up to now.',
      'The same money shows in the owner’s reports under games & rides.',
    ],
  },
  {
    path: 'appointments',
    title: 'Appointments',
    forWhom: 'A spa or salon’s desk and manager, and the super admin',
    lines: [
      'A spa or salon’s appointment book (e.g. Fin & Flex spa): today’s list, a day calendar, its services and the report.',
      'Super admin: All shops → Open shop on the spa → Appointments, beside its Home, Bills, Products and P&L.',
      'Book a free slot (with an advance if they pay now), then Arrive, Start, Done; take the balance, mark no-shows, cancel with the refund.',
      'The list and calendar refresh by themselves every half minute; every booking and payment shows at once.',
      'Managers set up the services (name, duration, turnaround, price), rooms or therapists and the opening hours under Services.',
    ],
    roles: {
      shop: {
        forWhom: 'The shop’s admin and counter logins',
        lines: [
          'Your shop’s appointment book: today’s list, the calendar, your services and the report.',
          'Counter logins read it; with “Book appointments” ticked by the admin they book, arrive / start / done and take payments.',
          'The list and calendar refresh by themselves every half minute.',
          'The admin sets up services, rooms and opening hours under Services; refunds are the admin’s too.',
        ],
      },
    },
  },
  {
    path: 'events',
    title: 'Events',
    forWhom: 'The cashier and the super admin',
    lines: [
      'Venue events with seats, tickets, money in and costs.',
      'Create an event, sell tickets and see how full it is; add a picture for the customer app.',
      'Published events show in the customer app until they end; Send to customers announces one.',
      'Ticket sales update by themselves; Attendees downloads the ticket list; Delete removes a ticket sold by mistake.',
      'Set the capacity first; sales stop when it is full.',
    ],
  },

  // ------------------------------------------------------------------ commerce (venue charges)
  {
    path: 'settlements',
    title: 'Shop settlements',
    forWhom: 'The super admin and the accountant',
    lines: [
      "Each tenant shop's monthly statement: commission, rent, cleaning, generator and penalties.",
      'Prepare drafts for the month, check them, Confirm all (another login than the one who prepared), record the payment.',
      'Rent reaches the ledgers when its statement is confirmed, or at once with Post rent to shops; new terms refresh the draft by themselves.',
      'Post rent to shops: pick a month, tick the shops, Post. A rent change posts only the difference; the statement never charges it twice.',
      'Nothing is booked until you confirm a draft.',
    ],
    roles: {
      shop_admin: {
        title: 'Venue statements',
        forWhom: 'Shop admins of tenant shops',
        lines: [
          'The monthly statements the venue sends your shop: rent, commission and other charges.',
          'Open a statement, print it, and see charges still to come this month.',
          'A new statement appears here by itself when the venue confirms it.',
          'Questions about a charge? Ask the venue office (super admin).',
        ],
      },
    },
  },
  {
    path: 'agreements',
    title: 'Rentals & rent holding',
    forWhom: 'The super admin and the accountant',
    lines: [
      "Each tenant shop's agreement: rent terms, deposit, power load and floor area.",
      'Add or edit an agreement and its dates; tap "Rent terms" to set fixed rent, commission or a share of revenue.',
      SAVED_AT_ONCE,
      'The monthly statement uses the agreement and the rent terms in force on each day.',
    ],
  },
  {
    path: 'dues',
    title: 'Dues from shops',
    forWhom: 'The super admin and the accountant',
    lines: [
      'What every shop owes the venue right now, from its ledger, with the oldest unpaid charge and the last payment.',
      'Record a payment, send a WhatsApp reminder, open the ledger, or (super admin) add a correction.',
      'Payments shops report with "I have paid" wait at the top: confirm them (the balance goes down) or reject with a reason.',
      'Part payments are fine: the balance goes down by exactly what was paid.',
    ],
  },
  {
    path: 'ledger',
    title: 'Shop ledgers',
    forWhom: 'The super admin and the accountant',
    lines: [
      "One shop's ledger: every charge the venue made and every payment, with the balance after each row.",
      'Pick the shop and a period (today … last year, or From–To); CSV and Excel carry the balance on From and To. Earlier records are badged.',
      'Charge shops: any amount the venue wants back from one, several or all shops — renovation, housekeeping, any work — one row each.',
      'Super admin: Edit or Delete a row a person entered; "Reverse for every shop" takes a Charge shops posting off every shop at once.',
      'Post rent puts a month’s rent on this ledger now; a rent change adds only the difference. Memo lines are not in the balance.',
    ],
  },
  {
    path: 'rent',
    title: 'Rent & venue charges',
    forWhom: 'Shop admins of tenant shops',
    lines: [
      'Your rent terms (fixed rent, commission or a share of sales, maybe above a monthly amount), what you owe, and your ledger.',
      'From the collection day a banner shows what is due until it is paid; after paying, tap "I have paid" with the UTR.',
      'See the rent building up this month, for the month or day by day, your generator and electricity charges.',
      'The balance updates as soon as the venue office confirms your payment or a statement.',
      'Grey memo lines show what the venue spent on your renovation; only rows with an amount are charged to you.',
    ],
  },
  {
    path: 'shop-maintenance',
    title: 'Maintenance',
    forWhom: 'Shop admins, and counters their admin allowed',
    lines: [
      'Raise a maintenance request for your shop with a photo; the venue team sees it at once.',
      'Follow each request: who is assigned, the planned date, and the cost once the job is done.',
      'Add a note for the team, or tap "Fixed, thank you" so they close the job.',
      'The venue may charge the cost to your ledger; it shows here and under Rent & venue charges.',
      'Requests and their status update by themselves.',
    ],
  },
  {
    path: 'closures',
    title: 'Closure register',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Days a shop stayed closed and the notice it gave.',
      'Record a closure, its notice, and waive a penalty if needed.',
      'New entries appear by themselves.',
      'Closures without enough notice add a penalty to the monthly statement.',
    ],
  },
  {
    path: 'advertisements',
    title: 'Ads I sell',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Advertising space and screen time sold at the venue (what you pay for advertising is under Marketing I buy).',
      'Make a quote, turn it into an invoice, record the payment.',
      'Changes show up by themselves.',
      'Print the invoice from the advertisement to give to the client.',
    ],
  },
  {
    path: 'cleaning-charges',
    title: 'Cleaning charges',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Cleaning done for a shop, charged on its monthly statement.',
      'Add a charge, or stop a repeating one.',
      'New charges appear by themselves.',
      'Charges added after a statement is confirmed go on the next month.',
    ],
  },
  {
    path: 'generator',
    title: 'Generator & cost allocation',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Generator runs, their cost, and how it is split between shops.',
      'Record a run; "Log & split" splits it at once, so each shop\'s share is on its ledger and its admins are told. Unsplit runs are flagged.',
      'Edit a run logged wrongly (date, times, fuel, costs) or Delete it, with a reason; a split run posts only the difference to each shop.',
      'Open a run to waive one shop\'s share; runs already on a confirmed statement cannot be changed (use a ledger adjustment).',
      'Shops set up without generator recovery are never charged.',
    ],
  },
  {
    path: 'electricity',
    title: 'Electricity bills',
    forWhom: 'The super admin and the accountant',
    lines: [
      "The venue's EB bill split by each shop's meter: previous and current reading, units, the rate per unit, the amount.",
      'Under Meters, enter every shop\'s meter and starting reading once; later bills fill the previous reading by themselves.',
      'New bill: the period, total units and amount give the rate; type each shop\'s current reading, then Post to shop ledgers.',
      'A posted bill can have its readings corrected (only the difference is charged) or be voided with a reason.',
      "Shops the venue runs itself are shown as the venue's own use and never charged; so are shops set up without recovery.",
    ],
  },

  // ------------------------------------------------------------------ facility
  {
    path: 'housekeeping',
    title: 'Housekeeping',
    forWhom: 'Housekeeping staff and supervisors',
    lines: [
      'The cleaning rota: what to clean, where and when.',
      'Tick off a task when it is done; supervisors check and sign it off.',
      'Tasks ticked on another phone show up by themselves.',
      'Add a photo when something needs a supervisor to look at it.',
    ],
  },
  {
    path: 'quality',
    title: 'Quality control',
    forWhom: 'Supervisors and the super admin',
    lines: [
      'Inspections of shops and areas with a checklist and a pass mark.',
      'Start an inspection, score each point, and see the latest score by shop.',
      'New inspections appear by themselves.',
      'Follow up quickly on anything that failed.',
    ],
  },
  {
    path: 'pest-control',
    title: 'Pest control',
    forWhom: 'Facility staff and the super admin',
    lines: [
      'Scheduled pest treatments across shops and common areas.',
      'Record a treatment and see what is due next, area by area.',
      'Updates appear by themselves.',
      'Overdue areas are shown first.',
    ],
  },
  {
    path: 'maintenance',
    title: 'Maintenance',
    forWhom: 'Maintenance staff, the super admin and the accountant',
    lines: [
      'Breakdown and routine jobs across the park and the shops: report, plan, assign, and close when fixed.',
      'Pick the type of job; a Routine job has a planned date and can repeat, making the next job when you complete it.',
      'Shop requests arrive with a "From shop" badge and a photo; the shop follows the status and can say "Fixed, thank you".',
      'On a completed job choose who pays: the venue (expense), charge the shop (its ledger, once) or the shop paid directly.',
      'New and updated jobs appear by themselves. Write what was done when you close a job, for the next person.',
    ],
  },
  {
    path: 'assets',
    title: 'Assets',
    forWhom: 'The super admin and facility managers',
    lines: [
      "The park's equipment with value, warranty, service dates and repairs.",
      'Add an asset, record a service or repair, see value by category.',
      'Changes show up by themselves.',
      'Fill in the warranty date so you are reminded before it ends.',
    ],
  },
  {
    path: 'other-services',
    title: 'Other services',
    forWhom: 'Facility staff and the super admin',
    lines: [
      'Chargeable services for shops, events and guests, and services bought from vendors.',
      'Record a service, who it was for and what it costs.',
      'New entries appear by themselves.',
      'Services for a shop can be added to its monthly statement.',
    ],
  },

  // ------------------------------------------------------------------ people
  {
    path: 'employees',
    title: 'Employees',
    forWhom: 'HR and the super admin',
    lines: [
      "The venue's own workers (shop staff are paid by their shop and are not here).",
      'Add or edit an employee, their department, salary and documents.',
      'Changes show up by themselves.',
      'Keep bank details filled in so payroll can be paid.',
    ],
  },
  {
    path: 'attendance',
    title: 'Attendance',
    forWhom: 'HR, supervisors, anyone the super admin gives the attendance module, and shop admins for their own shop',
    lines: [
      'Daily register: mark everybody for the day (Present, Half day, Absent, Leave, Week off); Mark all present fills the rest. Then Save.',
      "Day report: everyone's attendance for one day, with hours and overtime; print it or download CSV/Excel.",
      'Pick any past date with the arrows or the date box; today is the default.',
      'You see only the shops chosen for your login (Staff users → Edit → "Attendance for"); a shop admin sees their own shop.',
      'Add shop staff: pick a shop, then "Add its logins" or add a worker by name. Enter today and yesterday; HR managers any older day.',
    ],
  },
  {
    path: 'payroll',
    title: 'Payroll',
    forWhom: 'HR, the accountant and the super admin',
    lines: [
      'Monthly salaries worked out from attendance, with PF, ESI, advances and room rent.',
      'Prepare the month, check each payslip, then someone else approves it.',
      'Figures refresh by themselves while you work.',
      'The person who prepares payroll cannot approve it.',
    ],
  },
  {
    path: 'hr-reminders',
    title: 'HR payment reminders',
    forWhom: 'HR, payroll and the super admin',
    lines: [
      'Salaries, PF and ESI, advances to recover and other staff payments as they fall due.',
      'See what is due soon and mark it paid.',
      'New reminders appear by themselves.',
      'Check this at the start of each week.',
    ],
  },
  {
    path: 'accommodation',
    title: 'Accommodation',
    forWhom: 'HR',
    lines: [
      'Staff rooms and dormitories, and who stays where.',
      'Check a person into a room or out of it.',
      'Changes show up by themselves.',
      'Rent is taken from salary for every night stayed.',
    ],
  },

  // ------------------------------------------------------------------ supply chain
  {
    path: 'materials',
    title: 'Materials & stock',
    forWhom: 'The store and the accountant',
    lines: [
      "The venue's store: what is in stock and what it is worth.",
      'Receive, issue and count materials.',
      'Stock changes on another device appear by themselves.',
      'Count the store regularly and record differences.',
    ],
  },
  {
    path: 'material-requests',
    title: 'Material requests',
    forWhom: 'Departments, the store and the accountant',
    lines: [
      'Ask the store for materials and follow the request.',
      'Raise a request, approve it, and issue it from the store.',
      'Status changes appear by themselves.',
      'If the store has none, turn the request into a purchase order.',
    ],
  },
  {
    path: 'procurement',
    title: 'Procurement',
    forWhom: 'The accountant and the super admin',
    lines: [
      'Buying from suppliers: request, approval, purchase order, goods received.',
      'Create a purchase order, record what arrived, then add the supplier bill.',
      'Status changes appear by themselves.',
      'Print the purchase order to send to the supplier.',
    ],
  },
  {
    path: 'supplier-bills',
    title: 'Supplier bills',
    forWhom: 'The accountant and the super admin',
    lines: [
      "Supplier bills to check, approve and pay.",
      "Enter the supplier's bill, approve it (that books the expense), then record the payment.",
      'New bills and payments appear by themselves.',
      'Match the bill with the goods received before approving it.',
    ],
  },

  // ------------------------------------------------------------------ money
  {
    path: 'payments',
    title: 'Payments',
    forWhom: 'The cashier, the accountant and the super admin',
    lines: [
      'Every collection, online payment and refund.',
      'Filter by date, type or status; tap a payment for details or a refund.',
      'New payments appear by themselves.',
      'Only the accountant and the super admin can refund.',
    ],
  },
  {
    path: 'payments/:id',
    title: 'Payment',
    forWhom: 'The cashier, the accountant and the super admin',
    lines: [
      'One payment: amount, method, reference and what it was for.',
      'Print the receipt or refund it if you are allowed.',
      'Its status updates by itself (for example when UPI confirms).',
      'A refund is a new entry; the original payment is never changed.',
      'Something wrong with it? Report a mistake tells the owner, with a link to this payment.',
    ],
  },
  {
    path: 'closing',
    title: 'Daily closing',
    forWhom: 'Cashiers, shop admins, the accountant and the super admin',
    lines: [
      "Count the money at the end of the day and close the day's books.",
      'Enter the cash counted and the UPI and card totals; the page shows any difference; then Close day.',
      'The expected amounts update by themselves until you close.',
      'After closing, only the super admin can reopen a day.',
    ],
    roles: {
      shop: {
        title: 'Close the day',
        forWhom: 'Shop admins',
        lines: [
          "Close your shop's day: count the cash and check the takings.",
          'Enter the cash in the drawer and the UPI and card totals, check the difference, then Close day.',
          'Expected amounts update by themselves as bills are made.',
          'Close the day after the last bill, before you leave.',
        ],
      },
    },
  },
  {
    path: 'finance',
    title: 'Income & expenses',
    forWhom: 'The accountant, the cashier and the super admin',
    lines: [
      "The venue's income and expenses outside parking and shop bills.",
      'Add income or an expense, put it in a group, edit or reverse an entry. An expense may carry its items (Onion ½ kg ₹20).',
      'New entries appear by themselves.',
      'Entered by mistake? Delete removes it completely while its day is open; after closing, reverse it instead.',
      'Cannot change an entry yourself? Report on its row tells the owner what is wrong, with a link to it.',
    ],
    roles: {
      shop: {
        title: 'Expenses',
        forWhom: 'Shop admins',
        lines: [
          "Your shop's expenses: rent you pay, salaries, gas, supplies.",
          'Add an expense, edit it, or reverse a wrong one. New entries appear by themselves.',
          'Enter the whole purchase (Groceries ₹1,250) or add the items one by one (Onion ½ kg ₹20); both count in your expenses.',
          'Items bought shows what you spent on each item in the period.',
          'Every expense lowers your profit on Profit & loss, so add them all.',
        ],
      },
    },
  },
  {
    path: 'finance/groups',
    title: 'Account groups',
    forWhom: 'The accountant and the super admin',
    lines: [
      'Groups and subgroups used to sort income and expenses (e.g. Marketing › Social media).',
      'Add, rename or retire a group.',
      SAVED_AT_ONCE,
      'Keep the list short so everyone picks the same group.',
    ],
  },
  {
    path: 'reports',
    title: 'Reports',
    forWhom: 'The super admin, the accountant and the cashier',
    lines: [
      'Ready-made reports: collections, visits, shops and more.',
      'Pick a report and a period, then view or download it.',
      'Figures refresh by themselves while you look.',
      'Use Excel downloads for your accountant or auditor.',
    ],
  },
  {
    path: 'reports/wallet',
    title: 'Wallet & recharges',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Money loaded into customer wallets, money spent from them by shop, and the float the venue holds for customers.',
      'Pick a period: recharges by day and mode, wallet spending by shop and day, and what is owed to independent shops.',
      'Figures refresh by themselves while you look.',
      'A recharge is not income: the money stays the customer\'s until spent. Compare the float with the cash in hand.',
    ],
  },
  {
    path: 'points-history',
    title: 'Points history',
    forWhom: 'The super admin, the accountant and staff given it',
    lines: [
      'Every point added at the reception, spent at a counter, returned on a bill or adjusted, for the whole venue.',
      'Pick a period, a shop or a kind, search a name, mobile or bill no; open the bill or the customer; export CSV.',
      LIVE_SELF,
      'The totals at the top are for the whole filter, not just the rows on screen.',
    ],
    roles: {
      shop: {
        title: 'Points received',
        forWhom: 'The shop admin and staff given it',
        lines: [
          'The points customers paid with at your shop, and the points given back on returns.',
          'Pick a period, search a name, the last digits of a mobile or a bill no; export CSV for your accounts.',
          LIVE_SELF,
          'Mobile numbers show only their last 4 digits.',
        ],
      },
    },
  },

  // ------------------------------------------------------------------ customers
  {
    path: 'customers',
    title: 'Customers',
    forWhom: 'Gate staff, cashiers, the accountant and the super admin',
    lines: [
      'Find a customer by mobile number, name or vehicle number.',
      'Search, open a customer, or add a new one.',
      'New customers and changes appear by themselves.',
      'Searching by the last digits of the mobile is fastest.',
      'Super admin: Delete a customer (no points left, not inside); with history they are hidden and kept. Deleted customers → Restore.',
    ],
  },
  {
    path: 'customers/:id',
    title: 'Customer',
    forWhom: 'Gate staff, cashiers, the accountant and the super admin',
    lines: [
      'One customer: profile, vehicles, visits, wallet and coupons.',
      'Edit their details, link a vehicle, see payments, print a wallet statement.',
      'Wallet and visits update by themselves.',
      'Only change a mobile number when the customer asks you to.',
      'Delete customer: the super admin, or the reception for its own new customer within 15 minutes (a typo, nothing done yet).',
    ],
  },
  {
    path: 'customers/:id/statement',
    standalone: true,
    title: 'Wallet statement',
    forWhom: 'Cashiers and the super admin',
    lines: ["A printable statement of a customer's wallet.", 'Print it or save it as PDF.', 'It shows the wallet as it is when you open it.', 'Close it to go back.'],
  },
  {
    path: 'reception',
    title: 'Recharge wallet',
    forWhom: 'The reception cashier and the super admin',
    lines: [
      "Load money into a customer's wallet: they pay here by cash, UPI or card, then pay at the venue's counters from the wallet.",
      'Find the customer by mobile, name, vehicle number or app QR (or register them), pick the amount and how they paid, confirm.',
      "Today's recharges and the totals by cash, UPI and card update as you go; the cash counts in this counter's closing.",
      'A new customer gets a one-time setup code: they scan it on screen, or you send it on WhatsApp or print the slip.',
    ],
  },
  {
    path: 'reception-desk',
    title: 'Reception dashboard',
    forWhom: 'The reception cashier and the super admin',
    lines: [
      'Your start page at the reception: find a customer, see their vehicles, visits and points, add points and give the app setup code.',
      'Type a mobile, name or vehicle number (or scan their app QR or card). Nobody found? Register them with name, mobile and vehicle.',
      'Today (customers served, points added, cash, UPI and card taken, codes given) and the latest activity update as you work.',
      'Add points: the customer pays here, 1 point for every rupee (₹1,000 → 1,000 points); for UPI type the UTR. The receipt prints here.',
      'Added points by mistake? Undo your own top-up within 15 minutes while none are spent; the super admin can delete it completely.',
    ],
  },
  {
    path: 'reception/rides',
    title: 'Turbo cars & rides',
    forWhom: 'The reception cashier, the rides desk and the super admin',
    lines: [
      'Set up Turbo cars once (super admin). Sell 15 minutes at the price you type; extend with Add time; the car is freed when you stop the ride.',
      'Tap Sell ride: the ride, the minutes and price (or a preset), the customer (a new mobile is saved by itself), Start now or a time today.',
      'Every running ride counts down by itself; “Time up” turns amber, “Overdue” red with a beep. Stop it and collect the overtime.',
      'The header counts today per ride: “Rides 12 · ₹3,600”, the minutes and how many were extended. Today shows the money and the drawer.',
      'Add time at the ticket’s rate or a typed price; cancel an unstarted ride with a refund. No rides outlet? Ask the super admin to set it up.',
    ],
  },
  {
    path: 'cards',
    title: 'Cards & recharge',
    forWhom: 'The cashier and the super admin',
    lines: [
      'Wallet cards for customers: issue, recharge, block.',
      "Scan the customer's QR or card, recharge it, print the receipt or a statement.",
      'Balances update by themselves when the card is used.',
      'Block a lost card at once; its balance stays safe.',
    ],
  },
  {
    path: 'cards/receipts/:paymentId',
    standalone: true,
    title: 'Recharge receipt',
    forWhom: 'The cashier',
    lines: ['A printable receipt for a wallet recharge.', 'Print it for the customer.', 'It shows the recharge as recorded.', 'Close it to go back to Cards.'],
  },
  {
    path: 'coupons',
    title: 'Redeem coupon',
    forWhom: 'The cashier and the super admin',
    lines: [
      'Check and use a coupon a customer shows you.',
      'Scan the coupon or type its code, then redeem it.',
      'A coupon used elsewhere is refused straight away.',
      'Each coupon works once; the page tells you if it is expired.',
    ],
  },
  {
    path: 'campaigns',
    title: 'Campaigns',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Offers with a code for the shop counters, spend-based coupons and promotions, shown in the customer app.',
      'Only the super admin creates them: set the discount, shops, dates, limits and who may use it, then activate.',
      'Uses shows every bill that used an offer, per shop, with a download; Send to customers announces it in the app or on WhatsApp.',
      'Limits such as "first 100 customers" hold even when many counters bill at once.',
      'Delete a draft, or an ended campaign nobody used, with a reason; one with coupons or uses stays as history.',
    ],
  },
  {
    path: 'notifications',
    title: 'Notifications',
    forWhom: 'The super admin',
    lines: [
      'Offers and event news sent to customers in the app, by phone notification or on WhatsApp.',
      'Write a message, choose who gets it and when, then send or schedule it.',
      'Until WhatsApp is connected, nothing is sent on WhatsApp: download the share links and send them from the venue phone.',
      'Only customers who agreed to offers and did not opt out receive them; replying STOP on WhatsApp opts out.',
    ],
  },
  {
    path: 'feedback',
    title: 'Feedback',
    forWhom: 'Shop admins, the accountant and the super admin',
    lines: [
      'What guests say about the shops, the gate and the venue.',
      'Read comments, filter by shop or rating, and see the average rating.',
      'New feedback appears by itself.',
      'Reply quickly to low ratings.',
    ],
  },

  // ------------------------------------------------------------------ admin
  {
    path: 'users',
    title: 'Staff users',
    forWhom: 'The super admin',
    lines: [
      'Every login in the venue, grouped by shop.',
      'Add user → Reception login or Security login fills in the role, gate and password; or choose a role and shops yourself.',
      'Changes show up by themselves; the person gets them on their next page.',
      'Give each person their own login, never a shared one.',
      'Delete a login: signed out everywhere at once, the name is free again; logins with history are kept, marked deleted.',
    ],
    roles: {
      accountant: {
        title: 'Shop logins',
        forWhom: 'The accountant',
        lines: [
          "Each shop's admin and staff logins, grouped by shop.",
          'New shop login: choose Shop admin or Shop staff, the shop, and for staff tick what they may do on the access board.',
          'Edit, reset the password or delete any shop login; a new login can sign in at once.',
          'Other logins (super admin, reception, security, venue staff) are the super admin’s: ask them.',
        ],
      },
    },
  },
  {
    path: 'settings',
    title: 'Settings',
    forWhom: 'The super admin',
    lines: [
      'Business rules and switches for the whole venue.',
      'Turn features on or off and change rules.',
      'Printer for every shop: the model, position and width every shop’s counters take, new shops too, unless a shop sets its own.',
      'Changes apply at once and are written in the audit log.',
      'Change one thing at a time and check the result.',
    ],
  },
  {
    path: 'pricing',
    title: 'Pricing rules',
    forWhom: 'The super admin (the cashier can look)',
    lines: [
      'Parking charges per gate and vehicle type, for a venue that charges for parking.',
      'See the active rule; the super admin can make a new version and switch it on.',
      'A new rule applies to vehicles from the moment it is switched on.',
      'Parking is free at Carnival City, so this page only says so until charging is switched on under Settings.',
    ],
    roles: {
      cashier: {
        forWhom: 'The cashier',
        lines: [
          'The parking charges that apply today, per vehicle type.',
          'Look up the rate to answer a customer; only the super admin can change it.',
          'A change by the super admin shows here at once.',
          'The fee on Collect payment always follows this rule.',
        ],
      },
    },
  },
  {
    path: 'cameras',
    title: 'Cameras & number plates',
    forWhom: 'The super admin',
    lines: [
      'Gate cameras that read number plates and record entries and exits by themselves.',
      'Add a camera, test it, and see what it read.',
      'New reads appear by themselves; unclear ones go to the gate to confirm.',
      'Check the camera angle if many reads need confirming.',
    ],
  },
  {
    path: 'areas',
    title: 'Vehicle areas (RTO)',
    forWhom: 'The super admin',
    lines: [
      'Where visiting vehicles come from, from the RTO code on the plate (KL 07 = Ernakulam).',
      'Pick a period to see visits by district and state.',
      'Figures refresh by themselves while you look.',
      'Use it to plan where to advertise.',
    ],
  },
  {
    path: 'imports',
    title: 'Data import',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Bring in customers, vehicles and old records (shop sales, rent received, expenses, products) from Excel or CSV.',
      'Pick what to import, download its template, fill it and upload it.',
      'You see every row and its problems before anything is saved; the whole file goes in, or nothing.',
      'Every import is listed with who did it and when, and can be undone.',
    ],
  },
  {
    path: 'imports/history/:id',
    title: 'Old records import',
    forWhom: 'The super admin and the accountant',
    lines: [
      'One uploaded file of old shop sales, rent received or expenses, row by row.',
      'Rows with a problem say what to fix; fix them in your file and upload it again.',
      'Press Import when every row is ready: the whole file goes in together, or nothing.',
      'Undo removes the whole file again, unless a day has been closed or rent posted to the ledger since.',
    ],
  },
  {
    path: 'imports/:id',
    title: 'Import',
    forWhom: 'The super admin',
    lines: [
      'One uploaded file: its columns, rows and problems.',
      'Fix the column matching, check rows with problems, then import or discard.',
      'Progress updates by itself.',
      'Nothing is added until you press import.',
    ],
  },
  {
    path: 'terms',
    title: 'Terms & conditions',
    forWhom: 'The super admin',
    lines: [
      'The rules customers read and accept in the app, also shown on receipts.',
      'Write a new version and publish it.',
      'Customers see the new version the next time they open the app.',
      'Old versions are kept with who accepted them.',
    ],
  },
  {
    path: 'audit',
    title: 'Audit log',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Every sensitive change: who did it, when and what changed.',
      'Search by person, date or kind of change.',
      'New entries appear by themselves.',
      'Entries can never be edited or deleted.',
      '"Deleted entries" lists what was deleted completely: who, when, why and what disappeared (Details: every removed row).',
    ],
  },

  // ------------------------------------------------------------------ my business (the owner's own money)
  {
    path: 'venue/pnl',
    title: 'Venue P&L',
    forWhom: 'The super admin and the accountant',
    lines: [
      'Your own profit and loss: what the venue earned minus what it spent, one line per kind of money.',
      'Pick a period and tap any line to see the rows behind it; download Excel or CSV.',
      "Today's figures move as payments and entries come in; shop months not confirmed yet are shown as estimates.",
      'Open "Needs attention" first: it lists estimates, unmapped groups and jobs not yet in the accounts.',
    ],
  },
  {
    path: 'venue/marketing',
    title: 'Marketing I buy',
    forWhom: 'The super admin',
    lines: [
      'Advertising you pay for — theatre slides, hoardings, auto-backs, radio, social — with cost, payments and dues.',
      'Add a placement, then record each payment with its UTR and bill photo; mark it completed when it ends.',
      'Paid and due figures update as soon as a payment is recorded or reversed.',
      'Every payment is also an entry under Income & expenses, so the Venue P&L counts it once.',
      'Open a placement and use Add shops to the split to charge tenant shops a share; record each shop\'s payment there.',
    ],
  },
  {
    path: 'venue/works',
    title: 'Renovation & works',
    forWhom: 'The super admin',
    lines: [
      'Renovation, fit-out, signage and repair jobs per shop or area: budget against paid, photos. Period filter and CSV show paid in the range.',
      'Record each payment with what for (materials, wages…) and the item; "Add to the budget" raises it, else it shows over budget.',
      'Who pays: the shop, a split between shops (Add all shops equally, then edit), or Shop pays everything spent; charged on each ledger.',
      'Tick "Charge this to <shop>" on a payment to put it on that shop’s ledger; un-tick reverses it. Only differences are ever posted.',
      '"From <shop>": Charged · Collected · Paid directly · To collect. "Paid directly" = the shop paid the contractor itself: never collected.',
    ],
  },
  {
    path: 'venue/capital',
    title: 'Capital & loans',
    forWhom: 'The super admin; the accountant reads it',
    lines: [
      'Your capital account, loans and other liabilities: money you put in or took out, and what the business owes and pays back.',
      'Capital, drawings and loan principal are not income or expenses; the P&L shows only the interest and charges.',
      'Record payment: amount paid and how much is interest and charges; the principal is the rest. Correct one here, not under Income & expenses.',
      'Cash here is the owner’s own cash, not a counter till; daily closing is untouched. A day closed everywhere refuses interest and charges.',
      'Position: where you stand on any date, with every loan and its balance; print it on A4 or download CSV / Excel.',
    ],
  },
  {
    path: 'earlier-records',
    title: 'Earlier records',
    forWhom: 'The super admin, the accountant and logins given it',
    lines: [
      'Type in what was kept on paper before the system: shop rent and charges, money received, opening balances, works and petty cash.',
      'Each row sits on its own date, so ledgers and statements are complete. It never touches a till, a day closing or today’s figures.',
      'Rent of a month the system already charges is refused, so nothing counts twice. Use Edit or Delete with a reason; all is audited.',
      'Works: give the real status (ongoing, finished…), contractor payments, and what each shop was charged and paid, with dates.',
      'Pick a period (yesterday … last year, or From–To), shop, type or status; the totals and CSV follow the filter.',
    ],
  },

  // ------------------------------------------------------------------ everyone
  {
    path: 'account',
    title: 'My account',
    forWhom: 'Everyone',
    lines: [
      'Your own login: name, role and password.',
      'Change your password here.',
      'Changing it signs you out on your other devices.',
      'Use a password only you know, and never share your login.',
    ],
  },
  {
    path: 'guide',
    title: 'What I can do',
    forWhom: 'Everyone',
    lines: [
      'Your menu explained in one line each, and who to ask for more access.',
      'Tap any item to open it.',
      'The list follows what your login may do right now.',
      'Every page also has a ? button at the top with its own short help.',
    ],
  },

  // ------------------------------------------------------------------ printable pages (no menu)
  { path: 'login', standalone: true, title: 'Sign in', forWhom: 'Everyone', lines: ['Sign in with the login and password you were given.', 'Type your login and password, then Sign in.', 'Nothing here updates by itself.', 'Logins are not case-sensitive and spaces are ignored.'] },
  { path: 'visits/:id/pass', standalone: true, title: 'Entry pass', forWhom: 'Gate staff', lines: ['A printable entry pass for one vehicle.', 'Print it and hand it to the driver.', 'It shows the visit as recorded.', 'Close it to go back to the gate.'] },
  { path: 'bills/:id/print', standalone: true, title: 'Print bill', forWhom: 'Shop teams', lines: ['A printable copy of one bill.', 'Print it or save it as PDF.', 'It shows the bill as it is now, returns included.', 'Close it to go back.'] },
  { path: 'settlements/:id/print', standalone: true, title: 'Statement', forWhom: 'The venue office and shop admins', lines: ['A printable monthly statement.', 'Print it or save it as PDF.', 'It shows the statement as confirmed.', 'Close it to go back.'] },
  { path: 'advertisements/:id/invoice', standalone: true, title: 'Advertisement invoice', forWhom: 'The venue office', lines: ['A printable invoice for an advertisement.', 'Print it or save it as PDF for the client.', 'It shows the invoice as recorded.', 'Close it to go back.'] },
  { path: 'purchase-orders/:id/print', standalone: true, title: 'Purchase order', forWhom: 'The venue office', lines: ['A printable purchase order.', 'Print it or save it as PDF to send to the supplier.', 'It shows the order as recorded.', 'Close it to go back.'] },
  { path: 'supplier-bills/:id/print', standalone: true, title: 'Supplier bill', forWhom: 'The venue office', lines: ['A printable supplier bill with its payments.', 'Print it or save it as PDF.', 'It shows the bill as recorded.', 'Close it to go back.'] },
];
